import React, { useEffect, useRef } from "react";
import "./Careers.css";
import logo from "../assets/logo.png";
import safetyManager from "../assets/carrers_safety_manager.jpeg";
import seniorInspector from "../assets/carrers_senior_inspector.jpeg";
import hrManager from "../assets/carrers_HR.jpeg";

// Local hero image provided by developer
const HERO_IMAGE = "/mnt/data/745cbc2e-0bd2-4324-b362-3a925b7cae17.png";

// Benefits data
const benefits = [
  {
    id: 1,
    icon: "🎓",
    title: "Certification Support",
    desc: "We sponsor training and certifications with on-the-job mentorship.",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
  },
  {
    id: 2,
    icon: "🌐",
    title: "Global Standards",
    desc: "Work with ASME, API, AWS, ASTM and ISO-aligned projects.",
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
  },
  {
    id: 3,
    icon: "🔧",
    title: "Field Experience",
    desc: "Field-intensive roles with real operational exposure.",
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
  },
  {
    id: 4,
    icon: "📈",
    title: "Career Growth",
    desc: "Clear career progression and role mobility across projects.",
    gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
  },
  {
    id: 5,
    icon: "🤝",
    title: "Collaborative Culture",
    desc: "Safety-first environment with mutual respect and teamwork.",
    gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
  },
  {
    id: 6,
    icon: "💡",
    title: "Innovation Focus",
    desc: "Implement digital workflows and cutting-edge inspection solutions.",
    gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
  }
];

// Team values
const values = [
  { icon: "🎯", title: "Excellence", desc: "Uncompromising quality in every project" },
  { icon: "🛡️", title: "Safety", desc: "Highest safety standards in all operations" },
  { icon: "🤝", title: "Integrity", desc: "Ethical practices and transparency" },
  { icon: "🚀", title: "Innovation", desc: "Embracing technology for better solutions" },
];

// Staff data - Updated with correct names from your image
const teamStaff = [
  {
    id: 1,
    name: "Atish Babu",
    position: "Safety Manager",
    department: "Safety",
    experience: "10 years in safety management",
    location: "Based in Gujarat",
    image: safetyManager,
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    imagePosition: "center 20%"
  },
  {
    id: 2,
    name: "Mr. Harish Kumar Gupta",
    position: "Senior Inspector (CSWIP 3.1)",
    department: "QA/QC",
    experience: "16 years in pipeline and structural inspection",
    location: "Based in Gujarat",
    image: seniorInspector,
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    imagePosition: "center 15%"
  },
  {
    id: 3,
    name: "Stella Roselin",
    position: "HR Manager",
    department: "Admin",
    experience: "9 years in HR management",
    location: "Based in Andhra Pradesh",
    image: hrManager,
    gradient: "linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)",
    imagePosition: "center 25%"
  }
];

export default function Careers() {
  useEffect(() => {
    document.title = "Careers | Qnexa Services — Trusted by Industry Leaders";
  }, []);
  
  const email = "qnexservices@gmail.com";
  const whatsapp = "+91 8978418889";
  const heroRef = useRef(null);
  const benefitsRef = useRef(null);
  const teamRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) observer.observe(heroRef.current);
    if (benefitsRef.current) observer.observe(benefitsRef.current);
    if (teamRef.current) observer.observe(teamRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="q-careers-page">
      {/* Animated Background Elements */}
      <div className="bg-grid"></div>
      <div className="floating-shape shape-1"></div>
      <div className="floating-shape shape-2"></div>
      <div className="floating-shape shape-3"></div>

      {/* HERO SECTION */}
      <header className="careers-hero" ref={heroRef}>
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span>🚀</span>
              Join Our Team
            </div>
            <h1 className="hero-title">
              Build Your Future with
              <span className="gradient-text"> Qnexa Services</span>
            </h1>
            <p className="hero-subtitle">
              We're not just offering jobs—we're building careers for engineers, 
              inspectors, and innovators who want to make an impact in Oil & Gas, 
              Power, and Infrastructure industries.
            </p>

            <div className="hero-cta-group">
              <a 
                href={`mailto:${email}`} 
                className="cta-button primary"
              >
                <span>📧 Apply Now</span>
                <div className="button-glow"></div>
              </a>
              
              <a 
                href={`https://wa.me/${whatsapp.replace(/\D/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="cta-button secondary"
              >
                <span>💬 WhatsApp</span>
              </a>
            </div>

            
          </div>

          <div className="hero-visual">
            <div className="image-container">
              <img 
                src={HERO_IMAGE} 
                alt="Qnexa Services Team" 
                className="hero-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
                }}
              />
              <div className="image-overlay">
                <div className="overlay-text">
                  <h3>Join Excellence</h3>
                  <p>Be part of something bigger</p>
                </div>
              </div>
            </div>
            <div className="floating-card card-1">
              <div className="card-icon">🏆</div>
              <p>Industry Leaders</p>
            </div>
            <div className="floating-card card-2">
              <div className="card-icon">⭐</div>
              <p>Top Employers</p>
            </div>
          </div>
        </div>
      </header>

      {/* WHY JOIN US SECTION */}
      <section className="benefits-section" ref={benefitsRef}>
        <div className="section-header">
          <h2 className="section-title">
            Why <span className="highlight">Join</span> Qnexa?
          </h2>
          <p className="section-subtitle">
            Experience a workplace that values growth, innovation, and excellence
          </p>
        </div>

        <div className="benefits-grid">
          {benefits.map((benefit) => (
            <div 
              key={benefit.id}
              className="benefit-card"
              style={{ '--card-gradient': benefit.gradient }}
            >
              <div className="benefit-icon" style={{ backgroundImage: benefit.gradient }}>
                {benefit.icon}
              </div>
              <h3>{benefit.title}</h3>
              <p>{benefit.desc}</p>
              <div className="card-wave"></div>
            </div>
          ))}
        </div>
      </section>

      {/* MEET OUR TEAM SECTION */}
      <section className="team-section" ref={teamRef}>
        <div className="section-header">
          <h2 className="section-title">
            Meet Our <span className="highlight">Leadership Team</span>
          </h2>
          <p className="section-subtitle">
            Experienced professionals guiding our operations and ensuring quality standards
          </p>
        </div>

        <div className="team-container">
          {teamStaff.map((member) => (
            <div key={member.id} className="team-member-card">
              <div className="member-image-container">
                <div 
                  className="member-image-wrapper"
                  style={{ backgroundImage: member.gradient }}
                >
                  <img 
                    src={member.image} 
                    alt={`${member.name} - ${member.position}`}
                    className="member-image"
                    style={{ objectPosition: member.imagePosition }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80";
                    }}
                  />
                </div>
                <div className="member-badge">
                  <span className="badge-icon">⭐</span>
                  <span className="badge-text">Expert</span>
                </div>
              </div>
              
              <div className="member-info">
                <div className="member-header">
                  <h3 className="member-name">{member.name}</h3>
                  <div className="member-position">{member.position}</div>
                  <div className="member-department">{member.department}</div>
                </div>
                
                <div className="member-details">
                  <div className="detail-item">
                    <div className="detail-icon">📅</div>
                    <div className="detail-content">
                      <strong>Experience:</strong>
                      <p>{member.experience}</p>
                    </div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-icon">📍</div>
                    <div className="detail-content">
                      <strong>Location:</strong>
                      <p>{member.location}</p>
                    </div>
                  </div>
                </div>
                
                <div className="member-quote">
                  <div className="quote-icon">❝</div>
                  <p className="quote-text">
                    {member.department === "Admin" 
                      ? "Committed to fostering a supportive and growth-oriented work environment"
                      : "Committed to excellence and safety in every project we undertake"}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OUR VALUES SECTION */}
      <section className="values-section">
        <div className="values-container">
          <div className="values-content">
            <h2>Our Core Values</h2>
            <p className="values-intro">
              At Qnexa, our values guide everything we do—from project execution 
              to team collaboration and client relationships.
            </p>
            
            <div className="values-grid">
              {values.map((value, index) => (
                <div 
                  key={index}
                  className="value-item"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="value-icon">{value.icon}</div>
                  <h3>{value.title}</h3>
                  <p>{value.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="values-visual">
            <div className="rotating-circle-container">
              <div className="circle-border">
                <div className="circle-values">
                  <span className="value-text excellence">Excellence</span>
                  <span className="value-text safety">Safety</span>
                  <span className="value-text integrity">Integrity</span>
                  <span className="value-text innovation">Innovation</span>
                </div>
                
                <img src={logo} alt="Qnexa Logo" className="circle-logo" />
                
                <div className="circle-dot dot-1"></div>
                <div className="circle-dot dot-2"></div>
                <div className="circle-dot dot-3"></div>
                <div className="circle-dot dot-4"></div>
              </div>
              
              <div className="circle-description">
                <p>Our values are the foundation of everything we build</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APPLICATION PROCESS */}
      <section className="process-section">
        <h2>Simple Application Process</h2>
        
        <div className="process-steps">
          <div className="step">
            <div className="step-number">01</div>
            <h3>Send Your CV</h3>
            <p>Share your resume with us via email or WhatsApp</p>
          </div>
          
          <div className="step-connector"></div>
          
          <div className="step">
            <div className="step-number">02</div>
            <h3>Initial Screening</h3>
            <p>We'll review and get in touch within 48 hours</p>
          </div>
          
          <div className="step-connector"></div>
          
          <div className="step">
            <div className="step-number">03</div>
            <h3>Interview & Onboarding</h3>
            <p>Technical discussion and smooth onboarding process</p>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="final-cta">
        <div className="cta-container">
          <div className="cta-content">
            <h2>Ready to Build Your Future?</h2>
            <p>
              Join a team where your skills are valued, your growth is supported, 
              and your work makes a real difference in critical industries.
            </p>
            
            <div className="contact-info">
              <div className="contact-item">
                <div className="contact-icon">📧</div>
                <div>
                  <h4>Email Us</h4>
                  <a href={`mailto:${email}`}>{email}</a>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="contact-icon">💬</div>
                <div>
                  <h4>WhatsApp</h4>
                  <a 
                    href={`https://wa.me/${whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {whatsapp}
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="cta-action">
            <a 
              href={`mailto:${email}`}
              className="action-button"
            >
              <span>Start Your Journey</span>
              <div className="arrow">→</div>
            </a>
            <p className="cta-note">We typically respond within 24 hours</p>
          </div>
        </div>
      </section>
    </div>
  );
}